import React from 'react';
import { AuthContext } from '../contexts/AuthContext';
import WhatsAppAssistant from '../components/WhatsAppAssistant';
import Dashboard from './Dashboard';

/**
 * Banco de pruebas del asistente de WhatsApp. SOLO desarrollo: la ruta que lo
 * monta está detrás de `process.env.NODE_ENV === 'development'` en App.js, así
 * que no existe en el build de producción. Mismo motivo que [DevOnboarding].
 *
 * El asistente vive dentro del panel, detrás del login, y además decide cuál de
 * sus cinco pasos mostrar con una llamada a /api/whatsapp/onboarding-status.
 * Para revisar cómo se ve el paso 4 habría que tener cuenta, plan con la
 * función habilitada, backend levantado y haber completado los tres anteriores.
 *
 *   /dev/whatsapp?paso=1   elegir tipo de bot
 *   /dev/whatsapp?paso=2   configurar mensajes
 *   /dev/whatsapp?paso=3   probar el bot
 *   /dev/whatsapp?paso=4   conectar WhatsApp
 *   /dev/whatsapp?paso=5   ya configurado
 *
 * Cómo se fuerza el paso sin tocar el componente: se intercepta `fetch` y se
 * responde esa única ruta con el paso pedido. El resto de las llamadas siguen
 * saliendo como siempre —fallan sin backend, que es lo que el componente ya
 * maneja— así que no hay que agregarle props de prueba al código que se
 * publica.
 */

/**
 * Un hilo de prueba que toca TODOS los casos que la pantalla dibuja distinto.
 *
 * No es relleno: cada mensaje está para que se vea una rama del render. Sin
 * esto la maqueta muestra cuatro burbujas iguales y los casos que rompen —el
 * corte de día, el hito de sistema, los mensajes encadenados— no aparecen
 * nunca. Es la diferencia entre mirar la pantalla y verificarla.
 *
 *   cliente / bot / negocio   los tres autores, que se dibujan distinto
 *   sistema                   centrado y sin burbuja
 *   ayer y hoy                para que salga el corte de día
 *   dos seguidos del mismo    para que salga el encadenado sin avatar
 */
const ayer = (h, m) => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
};
const hoy = (h, m) => {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
};

const HILO = [
  { id: 'm1', autor: 'cliente', texto: 'Hola! Hacen coloración?', momento: ayer(18, 12) },
  { id: 'm2', autor: 'bot', texto: '¡Hola! 👋 Sí, hacemos coloración completa con tratamiento.', momento: ayer(18, 12) },
  { id: 'm3', autor: 'bot', texto: 'Sale $45.000 y lleva unas 2 horas. ¿Querés que te busque un turno?', momento: ayer(18, 13) },
  { id: 'm4', autor: 'cliente', texto: 'Dale, para el jueves si se puede', momento: ayer(18, 20) },
  { id: 'm5', autor: 'sistema', texto: 'Camila pidió hablar con una persona', momento: hoy(9, 2) },
  { id: 'm6', autor: 'cliente', texto: 'Perdón, una consulta: el tratamiento sirve para pelo teñido?', momento: hoy(9, 2) },
  { id: 'm7', autor: 'cliente', texto: 'Es que lo tengo bastante castigado y no quiero arruinarlo más', momento: hoy(9, 3) },
  { id: 'm8', autor: 'sistema', texto: 'Tomaste el control de la conversación', momento: hoy(9, 15) },
  { id: 'm9', autor: 'negocio', texto: 'Hola Camila! Sí, justamente el tratamiento es para eso.\nSi querés vení y lo miramos antes de decidir el color.', momento: hoy(9, 16) },
];

const LISTA = [
  {
    telefono: '+54 9 11 2345-6789', nombre: 'Camila Ruiz',
    ultimoTexto: 'Es que lo tengo bastante castigado y no quiero arruinarlo más',
    ultimoAutor: 'cliente', ultimoMomento: hoy(9, 3),
    necesitaHumano: true, enManosHumanas: true, sinLeer: 2,
  },
  {
    telefono: '+54 9 11 8765-4321', nombre: 'Martín Sosa',
    ultimoTexto: 'Perfecto, te espero el jueves a las 16:30.',
    ultimoAutor: 'negocio', ultimoMomento: hoy(8, 40),
    necesitaHumano: false, enManosHumanas: true, sinLeer: 0,
  },
  {
    telefono: '+54 9 11 5555-1234', nombre: 'Julieta Vega',
    ultimoTexto: 'Turno confirmado ✅ Viernes 11:45 · Corte',
    ultimoAutor: 'bot', ultimoMomento: hoy(7, 5),
    necesitaHumano: false, enManosHumanas: false, sinLeer: 0,
  },
  {
    // Sin nombre: la lista tiene que caer al teléfono, y el avatar a un dígito.
    telefono: '+54 9 351 444-2211', nombre: '',
    ultimoTexto: 'buenas, hasta que hora atienden?',
    ultimoAutor: 'cliente', ultimoMomento: ayer(21, 30),
    necesitaHumano: false, enManosHumanas: false, sinLeer: 1,
  },
];

/**
 * Respuestas de mentira, en ORDEN: la primera que coincide gana.
 *
 * El orden importa y no es un detalle de estilo: `/conversaciones/<telefono>`
 * y `/conversaciones` se cumplen las dos para la URL de un hilo, así que la
 * más específica tiene que ir primero o la lista se come al hilo.
 */
const RESPUESTAS = (paso) => ([
  // Las acciones contestan OK y no cambian nada: acá se mira cómo se ve, y el
  // comportamiento de verdad está probado contra el backend. El DELETE se
  // registra en `window.__ultimoBorrado` para poder verificar desde afuera que
  // el botón pide lo que dice pedir.
  [(u, o) => u.includes('/api/whatsapp/conversaciones/') && o?.method === 'DELETE',
    { ok: true, borrados: 12 }],
  [(u, o) => u.includes('/api/whatsapp/conversaciones/') && o?.method === 'POST', { ok: true }],
  [(u) => /\/api\/whatsapp\/conversaciones\/[^/]+$/.test(u.split('?')[0]), { mensajes: HILO }],
  [(u) => u.includes('/api/whatsapp/conversaciones'), {
    conversaciones: LISTA,
    esperando: LISTA.filter((c) => c.necesitaHumano).length,
    configurado: true,
  }],
  [(u) => u.includes('/api/whatsapp/onboarding-status'), { completed: paso >= 5, currentStep: paso }],
  [(u) => u.includes('/api/whatsapp/connection'), { connected: paso >= 5, phoneNumber: '+54 9 11 6543-2109' }],
  [(u) => u.includes('/api/business'), {
    name: 'Peluquería y Estética Integral Doña Rosa',
    services: [
      { id: 's1', name: 'Corte de pelo y peinado', duration: 45, price: 12000 },
      { id: 's2', name: 'Coloración completa con tratamiento', duration: 120, price: 45000 }
    ]
  }],
]);

let yaInterceptado = false;

function interceptar(paso) {
  if (yaInterceptado) return;
  yaInterceptado = true;
  const original = window.fetch.bind(window);
  const tabla = RESPUESTAS(paso);
  window.fetch = async (entrada, opciones) => {
    const url = typeof entrada === 'string' ? entrada : entrada.url;
    if (opciones?.method === 'DELETE') window.__ultimoBorrado = url;
    const fila = tabla.find(([coincide]) => coincide(url, opciones));
    if (fila) {
      return new Response(JSON.stringify(fila[1]), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }
    return original(entrada, opciones);
  };
}

export default function DevWhatsApp() {
  const params = new URLSearchParams(window.location.search);
  const paso = Number(params.get('paso')) || 1;
  // `?dentro=panel` lo monta dentro del panel, que es donde vive de verdad.
  // Importa porque suelto ocupa toda la ventana y dentro del panel comparte
  // caja con el menú lateral y los rellenos de .dashboard-section: los
  // problemas de alto y de scroll solo aparecen ahí.
  const dentroDelPanel = params.get('dentro') === 'panel';
  interceptar(paso);

  const sesionFalsa = {
    currentUser: {
      uid: 'dev-uid',
      email: 'dev@aturno.test',
      emailVerified: true,
      getIdToken: async () => 'token-de-prueba'
    },
    userData: { onboardingCompleted: true },
    refreshUserData: async () => {},
    loading: false
  };

  return (
    <AuthContext.Provider value={sesionFalsa}>
      {dentroDelPanel ? (
        <Dashboard isDemo={true} />
      ) : (
        // Suelto no hay `.dashboard-section` que ponga el relleno, así que lo
        // pone esta ruta: el asistente ya no trae relleno propio.
        <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
          <WhatsAppAssistant />
        </div>
      )}
    </AuthContext.Provider>
  );
}
